// Pure formatting helpers for the document detail overlay. No IO, no React —
// safe to import from pure leaf components and their stories.

import type { CaseDocumentImportInfo } from '@/services/api/cases'

export function formatBytes(bytes: number): string {
  if (!bytes) return '0 B'
  const units = ['B', 'KB', 'MB', 'GB', 'TB']
  let i = 0
  let v = bytes
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024
    i++
  }
  return `${v.toFixed(v >= 100 || i === 0 ? 0 : 1)} ${units[i]}`
}

/** Collapse a sorted page list into a compact display: "1, 3-5, 7". */
export function formatPageList(pages: number[]): string {
  if (!pages.length) return ''
  const sorted = [...pages].sort((a, b) => a - b)
  const runs: string[] = []
  let start = sorted[0]
  let prev = sorted[0]
  for (let i = 1; i < sorted.length; i++) {
    const n = sorted[i]
    if (n === prev + 1) {
      prev = n
      continue
    }
    runs.push(start === prev ? `${start}` : `${start}-${prev}`)
    start = n
    prev = n
  }
  runs.push(start === prev ? `${start}` : `${start}-${prev}`)
  return runs.join(', ')
}

/** Human title for an import scope: pages / tabular rows×cols / whole doc. */
export function importScopeTitle(scope: CaseDocumentImportInfo): string {
  if (scope.scope_type === 'pages' && scope.selected_pages?.length) {
    const n = scope.selected_pages.length
    return `Imported with ${n} page${n === 1 ? '' : 's'} selected`
  }
  if (scope.scope_type === 'tabular') {
    const r = scope.selected_rows?.length ?? 0
    const c = scope.selected_columns?.length ?? 0
    return `Imported with ${r} row${r === 1 ? '' : 's'} × ${c} column${c === 1 ? '' : 's'} selected`
  }
  return 'Imported as whole document'
}

export function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—'
  try {
    return new Date(iso).toLocaleDateString()
  } catch {
    return iso
  }
}

export function formatTime(iso: string | null | undefined): string | undefined {
  if (!iso) return undefined
  try {
    return new Date(iso).toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    })
  } catch {
    return undefined
  }
}
